import { createState } from "@/state.init";
import { UserAccount, AccountFilters, SEED, AccountsState } from "./models";
import { UseLoading } from "@/common/composition/loading";
import * as api from "./api";
import { UseSearch } from "@/common/composition/search";
import { computed } from "@vue/composition-api";
import { SortBy } from "@/common/models";

const { isLoading, setLoading } = UseLoading(false);

const { getter, mutation, action, m } = createState("accounts", SEED);

//#region === LAST CURSOR ===
const lastCursor = getter((s) => s.lastCursor);
const setLastCursor = m.set("lastCursor");
const clearCursor = m.always("lastCursor", "");
//#endregion

//#region  === HAS NO RESULTS ===
const hasNoResults = getter((s) => s.hasNoResults);
const setHasNoResults = m.set("hasNoResults");
//#endregion

//#region  === FILTERS ===
const filters = getter((s) => s.filters);
const updateFilters = mutation<AccountFilters>(
  "update filters",
  (s, filters) => {
    return {
      ...s,
      filters,
    };
  }
);
//#endregion

const setAllAccountsHasLoaded = m.set("allAccountsHasLoaded");

//#region  === TOTAL ACCOUNTS COUNT ===
const totalAccountsCount = getter((s) => s.totalNumOfAccounts);
const setTotalAccountsCount = m.set("totalNumOfAccounts");
//#endregion

//#region === ACCOUNTS ===
// <Account, "accounts">
const accounts = m.crud<UserAccount, "accounts">("accounts", "id");

const reset = action("rest", async ({ mutate }) => {
  mutate(accounts.set, []);
  mutate(setIsCheckedAllAccounts, false);
});

const getAccounts = action<void>("get Accounts", async ({ mutate, state }) => {
  setLoading(true);
  mutate(setHasNoResults, false);
  mutate(setAllAccountsHasLoaded, false);
  // @ts-ignore
  mutate(clearCursor);
  const res = await api.getAccounts({
    lastCursor: "",
    textQuery: state.filters.query,
    sort: state.sortBy,
  });
  if (res.success) {
    const { count, objects, cursor } = res.value;

    mutate(setLastCursor, cursor);
    mutate(
      accounts.set,
      objects.map((a) => ({ ...a, checked: false }))
    );
    mutate(setAllAccountsHasLoaded, !res.value.hasMore);
    mutate(setTotalAccountsCount, count);
    if (objects.length === 0) {
      mutate(setHasNoResults, true);
    }
  }
  setLoading(false);
});

const loadMoreAccounts = action("get Accounts", async ({ mutate, state }) => {
  if (
    state.allAccountsHasLoaded ||
    isLoading.value ||
    state.accounts.length >= totalAccountsCount.value
  ) {
    return;
  }
  setLoading(true);
  const res = await api.getAccounts({
    lastCursor: state.lastCursor,
    textQuery: state.filters.query,
    sort: state.sortBy,
  });
  if (res.success) {
    if (res.value.cursor === state.lastCursor) {
      mutate(setAllAccountsHasLoaded, true);
    } else {
      mutate(
        accounts.append,
        res.value.objects.map((a) => ({
          ...a,
          checked: state.isCheckedAllAccounts,
        }))
      );
      mutate(setLastCursor, res.value.cursor);
    }
    mutate(setAllAccountsHasLoaded, !res.value.hasMore);
  }
  setLoading(false);
});
//#endregion

//#region  === CHECKED ROWS ===
const isCheckedAllAccounts = getter((s) => s.isCheckedAllAccounts);
const setIsCheckedAllAccounts = mutation<boolean>(
  "set isCheckedAllAccounts",
  (s, isCheck) => {
    return {
      ...s,
      isCheckedAllAccounts: isCheck,
      accounts: s.accounts.map((a: UserAccount) => ({
        ...a,
        checked: isCheck,
      })),
    };
  }
);
const onAccountChecked = action<UserAccount>(
  "on Account checked",
  async ({ mutate, state }, account) => {
    mutate(
      accounts.set,
      state.accounts.map((a: UserAccount) => {
        if (a.id === account.id) {
          a.checked = !a.checked;
        }
        return a;
      })
    );
  }
);
//#endregion

//#region   === SEARCH ACCOUNTS ===
const searchLastCursor = getter((s) => s.searchLastCursor);
const setSearchLastCursor = m.set("searchLastCursor");
const clearSearchLastCursor = m.always("searchLastCursor", "");
const {
  isSearching,
  clearSearch,
  results: searchResults,
  query,
  loadMore: loadMoreSearchResults,
  noResults: noSearchResults,
} = UseSearch(async (query: string) => {
  setLoading(true);
  clearSearchLastCursor();
  const res = await api.getAccounts({
    lastCursor: "",
    sort: sortBy.value,
    textQuery: query,
  });
  setLoading(false);
  if (res.success) {
    setSearchLastCursor(res.value.cursor);
  }
  if (res.success) {
    return { objects: res.value.objects, cursor: res.value.cursor };
  } else {
    return { objects: [], cursor: "" };
  }
});
//#endregion

const displayAccounts = computed(() =>
  isSearching.value ? searchResults.value : accounts.read.value
);

//#region  === ACCOUNT ACTIONS ===
const {
  isLoading: isPerformingAction,
  setLoading: setIsPerformingAction,
} = UseLoading(false);

const toggleAccountScanState = action<UserAccount>(
  "change account scan state",
  async ({ mutate }, account) => {
    setIsPerformingAction(true);
    await api.changeAccountScan(account, !account.isScanned);
    account.isScanned = !account.isScanned;
    mutate(accounts.update, account);
    setIsPerformingAction(false);
  }
);
//#endregion

//#region === SCAN ===
const getSelectedItems = (
  state: AccountsState
): { selectAll: boolean; ids: (number | string)[] } => {
  const { accounts, isCheckedAllAccounts: globalCheck } = state;
  const ids = accounts.filter((f) => f.checked != globalCheck).map((f) => f.id);
  return {
    selectAll: state.isCheckedAllAccounts,
    ids,
  };
};
const scanAll = action("scan all", async ({ mutate, state, dispatch }) => {
  setIsPerformingAction(true);
  const { selectAll, ids } = getSelectedItems(state);
  await api.scanAll({ selectAll, ids });
  mutate(
    accounts.set,
    state.accounts.map((a: UserAccount) => {
      if (selectAll) {
        if (ids.includes(a.id)) {
          return a;
        } else {
          a.isScanned = true;
          return a;
        }
      } else {
        if (ids.includes(a.id)) {
          a.isScanned = true;
          return a;
        } else {
          return a;
        }
      }
    })
  );
  setIsPerformingAction(false);
  mutate(setIsCheckedAllAccounts, false);
});

const removeScanFromAll = action(
  "remove scan from all",
  async ({ mutate, state }) => {
    setIsPerformingAction(true);
    const { selectAll, ids } = getSelectedItems(state);
    await api.removeScanFromAll({ selectAll, ids });
    mutate(
      accounts.set,
      state.accounts.map((a: UserAccount) => {
        if (selectAll) {
          if (ids.includes(a.id)) {
            return a;
          } else {
            a.isScanned = false;
            return a;
          }
        } else {
          if (ids.includes(a.id)) {
            a.isScanned = false;
            return a;
          } else {
            return a;
          }
        }
      })
    );
    setIsPerformingAction(false);
    mutate(setIsCheckedAllAccounts, false);
  }
);
//#endregion

//#region === SORT ===
const sortBy = getter((s) => s.sortBy);
const setSortBy = m.set("sortBy");
const onSortingChanged = action<SortBy>(
  "on sorting changed",
  async ({ mutate, dispatch }, sortBy) => {
    mutate(setSortBy, sortBy);
    mutate(setIsCheckedAllAccounts, false);
    // @ts-ignore
    dispatch(reset);
    // @ts-ignore
    dispatch(getAccounts);
  }
);
//#endregion

export {
  isLoading,
  hasNoResults,
  filters,
  updateFilters,
  totalAccountsCount,
  getAccounts,
  loadMoreAccounts,
  // check state
  isCheckedAllAccounts,
  setIsCheckedAllAccounts,
  onAccountChecked,
  // search
  isSearching,
  searchResults,
  noSearchResults,
  query,
  clearSearch,
  displayAccounts,
  // actions
  isPerformingAction,
  toggleAccountScanState,
  scanAll,
  removeScanFromAll,
  onSortingChanged,
};
